import JSZip from 'jszip';
import { kmlWithFolders } from '@tmcw/togeojson';

/**
 * Parse a KMZ or KML file into a folder-grouped structure.
 * Returns an array of "groups" — one per KML folder (or a root group
 * for ungrouped placemarks) — each containing its GeoJSON features,
 * a display name, and extracted style color.
 */
export async function parseKmzFile(file) {
  const extension = file.name.split('.').pop().toLowerCase();

  let kmlString;

  if (extension === 'kmz') {
    const arrayBuffer = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);
    const kmlEntry = Object.keys(zip.files).find(name => name.endsWith('.kml'));
    if (!kmlEntry) {
      throw new Error('No .kml file found inside the KMZ archive');
    }
    kmlString = await zip.files[kmlEntry].async('string');
  } else if (extension === 'kml') {
    kmlString = await file.text();
  } else {
    throw new Error(`Unsupported file type: .${extension}. Use .kmz or .kml files.`);
  }

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(kmlString, 'text/xml');

  const parseError = xmlDoc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Failed to parse KML: invalid XML');
  }

  const tree = kmlWithFolders(xmlDoc);
  const groups = [];

  collectGroups(tree, groups, []);

  if (groups.length === 0) {
    throw new Error('No geographic features found in the file');
  }

  const totalFeatures = groups.reduce((sum, g) => sum + g.features.length, 0);

  return {
    groups,
    filename: file.name,
    featureCount: totalFeatures,
  };
}

function extractFeatureColor(feature) {
  const props = feature.properties || {};
  return props.stroke || props.fill || null;
}

/**
 * Walk the kmlWithFolders tree and collect groups.
 * Each KML Folder becomes a group; bare features at the root also get grouped.
 */
function collectGroups(node, groups, path) {
  if (!node || !node.children) return;

  const bareFeatures = [];

  for (const child of node.children) {
    if (child.type === 'folder') {
      const folderName = child.meta?.name || 'Unnamed Folder';
      const folderPath = [...path, folderName];
      const features = flattenFeatures(child);

      if (features.length > 0) {
        const color = extractFeatureColor(features[0]);
        groups.push({
          name: folderName,
          path: folderPath.join(' / '),
          features,
          featureCount: features.length,
          color,
        });
      }

      // Also recurse to find sub-folders
      collectGroups(child, groups, folderPath);
    } else if (child.type === 'Feature' && child.geometry) {
      bareFeatures.push(child);
    }
  }

  if (bareFeatures.length > 0 && path.length === 0) {
    groups.push({
      name: 'Other Features',
      path: 'Other Features',
      features: bareFeatures,
      featureCount: bareFeatures.length,
      color: extractFeatureColor(bareFeatures[0]),
    });
  } else if (bareFeatures.length > 0) {
    // Bare features inside a folder already handled by parent call,
    // but only if the parent folder itself didn't contain sub-folders
    // that already created groups. Attach to a group for the current path.
    const pathStr = path.join(' / ');
    const existing = groups.find(g => g.path === pathStr);
    if (existing) {
      existing.features.push(...bareFeatures);
      existing.featureCount = existing.features.length;
    }
  }
}

/**
 * Flatten all Feature nodes out of a folder subtree (non-recursive into sub-folders,
 * since sub-folders get their own groups).
 */
function flattenFeatures(node) {
  const features = [];
  if (!node.children) return features;
  for (const child of node.children) {
    if (child.type === 'Feature' && child.geometry) {
      features.push(child);
    }
  }
  return features;
}
